import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SelectItem } from 'primeng/api';

import { Observable, Subject, takeUntil } from 'rxjs';

import { ValidationConstants, ValidationService } from 'sc-common';
import { ApiEditingToolParsedAuthor } from 'sc-common/core/services/open-api/main-api-client';
import { ApiGridOrganization } from 'sc-common/core/services/open-api/shared-api-client';

@Component({
    selector: 'et-author-entry',
    templateUrl: './author-entry.component.html',
    styleUrls: ['./author-entry.component.scss'],
    standalone: false
})
export class AuthorEntryComponent implements OnInit, OnDestroy {

    @Input()
    public author: ApiEditingToolParsedAuthor;

    @Input()
    public isOrcidIdUnusedExpression: (orcidId: string) => Observable<boolean>;

    @Input()
    public isEmailUnusedExpression: (email: string) => Observable<boolean>;

    @Input()
    public titles$: Observable<SelectItem[]>;

    @Input()
    public countries$: Observable<SelectItem[]>;

    public isReadonly: boolean;

    public isEmailUnused: boolean;

    @Output()
    public readonly authorAdded = new EventEmitter<ApiEditingToolParsedAuthor>();

    @Output()
    public readonly authorRemoved = new EventEmitter<ApiEditingToolParsedAuthor>();

    public form: UntypedFormGroup;

    public requestInProcess = false;

    private readonly _destroy$ = new Subject<void>();

    constructor(private readonly _formBuilder: UntypedFormBuilder) {
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public ngOnInit(): void {
        this.isReadonly = !!this.author.memberId;
        this._buildForm();
    }

    public addAuthor(): void {
        if (!this.isReadonly && this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        if (this.isEmailUnusedExpression) {
            this.isEmailUnusedExpression(this.author.email)
                .pipe(takeUntil(this._destroy$))
                .subscribe((isMemberEmailUnused: boolean) => {
                    if (isMemberEmailUnused) {
                        this._addAuthorEmit()
                    } else {
                        this.isEmailUnused = !isMemberEmailUnused;
                    }
                })
            return;
        }

        this._addAuthorEmit();
    }

    private _addAuthorEmit(): void {
        this.requestInProcess = true;

        const emitValue = this.isReadonly ? this.author : Object.assign(this.author, this.form.value);
        this.authorAdded.emit(emitValue);
    }

    public removeAuthor(): void {
        this.authorRemoved.emit(this.author);
    }

    public fillAddressFromGrid(gridAddress: ApiGridOrganization): void {

        this.form.patchValue({
            countryId: gridAddress.countryCode
        });

        this.form.markAllAsTouched();
    }

    private _buildForm(): void {

        const nameValidators = [
            Validators.required,
            ValidationService.nameValidator,
            Validators.maxLength(ValidationConstants.Max500Length)];

        const organizationValidators = [
            Validators.required,
            Validators.maxLength(ValidationConstants.Max200Length),
            ValidationService.alphaNumSymbolValidator];

        const orcidValidators = {
            validators: ValidationService.orcidValidator,
            asyncValidators: ValidationService.isOrcidIdUnusedValidator(orcidId => this.isOrcidIdUnusedExpression(orcidId))
        };

        this.form = this._formBuilder.group({
            firstName: [this.author?.firstName, this.isReadonly ? null : nameValidators],
            lastName: [this.author?.lastName, this.isReadonly ? null : nameValidators],
            countryId: [this.author?.countryId, this.isReadonly ? null : Validators.required],
            universityOrOrganization: [this.author?.universityOrOrganization, this.isReadonly ? null : organizationValidators],
            title: [this.author?.title],
            orcidId: [this.author?.orcidId, this.isReadonly ? null : orcidValidators]
        });

        if (this.isReadonly) {
            this.form.disable();

            // to make form enabled we need at least one control enabled
            this.form.addControl('dummy', this._formBuilder.control([]));
        }
    }
}
